import {logger} from '@zoefin/logger';
import {forceFlushOpenTelemetryLogger} from '@zoefin/logger/otel';

import {
  DEFAULT_JSON_HEADERS,
  MAX_LOG_ATTRIBUTE_LENGTH,
} from '../constants/request';
import {RequestError} from '../errors/requestError';
import {
  buildRequestFailureLogMessage,
  classifyRequestFailure,
} from '../errors/utils';
import {resolveRequest} from '../resolve';
import {
  getBrowserPageName,
  getBrowserSessionMetadata,
} from '../runtime/browser';
import {createRequestTimeoutSignal} from '../runtime/signal';
import {
  flushSpansOnError,
  injectTracingHeaders,
  setResponseAttributes,
  setSpanError,
  startRequestSpan,
  withRequestSpanContext,
} from '../tracing';
import {
  type RequestFailureLogContext,
  type RequestMethod,
  type RequestOptions,
  type RequestRawResult,
  RequestResponseType,
} from '../types/request';
import type {ResolvedRequest} from '../types/route';
import {RequestAuthMode, RequestAuthTokenSource} from '../types/route';
import {isFormDataBody, serializeRequestBody} from '../utils/body';
import {normalizeHeadersInit, sanitizeHeadersForLog} from '../utils/headers';
import {isJsonLikeText} from '../utils/json';
import {redactSensitiveValue, sanitizeUrlForLog} from '../utils/redaction';
import {generateValidUrl} from '../utils/url';

const readResponseBody = async (
  response: Response,
  responseType?: RequestResponseType,
): Promise<unknown> => {
  // STREAM consumers read response.body themselves; draining here would
  // lock the ReadableStream and break the piping use case (chat relay).
  if (responseType === RequestResponseType.STREAM) {
    return undefined;
  }

  if (response.status === 204 || response.status === 205) {
    return undefined;
  }

  if (responseType === RequestResponseType.BLOB) {
    return response.blob();
  }

  const text = await response.text();

  if (!text) {
    return undefined;
  }

  if (responseType === RequestResponseType.TEXT) {
    return text;
  }

  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json') || isJsonLikeText(text)) {
    return JSON.parse(text) as unknown;
  }

  return text;
};

const parseResponseBody = async (
  response: Response,
  responseType?: RequestResponseType,
): Promise<unknown> => {
  try {
    return await readResponseBody(response, responseType);
  } catch (error) {
    logger.warn(
      {
        error,
        status: response.status,
      },
      'Failed to parse response body',
    );

    return undefined;
  }
};

const flushLogs = (): void => {
  forceFlushOpenTelemetryLogger().catch(() => undefined);
};

const withDefaultHeaders = ({
  headers,
  body,
}: {
  headers?: HeadersInit;
  body?: RequestOptions['body'];
}): Headers => {
  const normalizedHeaders = normalizeHeadersInit(headers) ?? {};
  const finalHeaders = new Headers({
    ...DEFAULT_JSON_HEADERS,
    ...normalizedHeaders,
  });

  if (isFormDataBody(body)) {
    finalHeaders.delete('Content-Type');
  }

  return finalHeaders;
};

const attachBrowserMetadata = (headers: Headers): void => {
  const pageName = getBrowserPageName();
  const browserSessionMetadata = getBrowserSessionMetadata();

  if (pageName) {
    headers.set('pageName', pageName);
  }

  if (browserSessionMetadata.ddSessionId) {
    headers.set('dd_session_id', browserSessionMetadata.ddSessionId);
  }

  if (browserSessionMetadata.fsSessionId) {
    headers.set('fs_session_id', browserSessionMetadata.fsSessionId);
  }
};

const buildRequestInit = ({
  method,
  headers,
  options,
}: {
  method: RequestMethod;
  headers: Headers;
  options?: RequestOptions;
}): RequestInit => {
  const serializedBody = serializeRequestBody(options?.body);

  return {
    method,
    headers,
    ...(options?.cache && {cache: options.cache}),
    ...(options?.credentials && {credentials: options.credentials}),
    ...(options?.signal && {signal: options.signal}),
    ...(options?.next && {next: options.next}),
    ...(serializedBody !== undefined && {body: serializedBody}),
  };
};

const getStringField = (obj: object, key: string): string | undefined => {
  if (!(key in obj)) return undefined;

  const value = (obj as Record<string, unknown>)[key];

  return typeof value === 'string' && value ? value : undefined;
};

const extractErrorMessage = (parsedData: unknown): string | undefined => {
  if (typeof parsedData === 'string') {
    return parsedData;
  }

  if (typeof parsedData !== 'object' || parsedData === null) {
    return undefined;
  }

  const directMessage = getStringField(parsedData, 'message');

  if (directMessage) return directMessage;

  if (
    'throws' in parsedData &&
    typeof parsedData.throws === 'object' &&
    parsedData.throws !== null
  ) {
    return (
      getStringField(parsedData.throws, 'message') ??
      getStringField(parsedData, 'error')
    );
  }

  return getStringField(parsedData, 'error');
};

/**
 * Builds a RequestError object for a failed request.
 * @param response - The response from the request.
 * @param requestUrl - The URL of the request.
 * @param method - The method of the request.
 * @param parsedData - The parsed data from the request.
 * @returns
 */
const buildResponseError = ({
  response,
  requestUrl,
  method,
  parsedData,
}: {
  response: Response;
  requestUrl: URL;
  method: RequestMethod;
  parsedData: unknown;
}): RequestError => {
  const requestId =
    response.headers.get('x-request-id') ??
    response.headers.get('x-correlation-id') ??
    undefined; // coerce null → undefined for RequestError.requestId
  const message =
    extractErrorMessage(parsedData) ||
    response.statusText ||
    `Request failed with status ${response.status}`;

  return new RequestError({
    message,
    status: response.status,
    response,
    data: parsedData,
    url: requestUrl.toString(),
    method,
    requestId,
  });
};

const buildMissingClientCredentialsError = ({
  requestUrl,
  method,
}: {
  requestUrl: URL;
  method: RequestMethod;
}): RequestError => {
  return new RequestError({
    message: 'Client credentials token resolution failed',
    status: 401,
    url: requestUrl.toString(),
    method,
  });
};

const toFailureLogContext = (
  error: RequestError,
): RequestFailureLogContext => ({
  message: error.message,
  status: error.status,
  url: sanitizeUrlForLog(error.url),
  method: error.method,
  requestId: error.requestId,
});

/** Truncates a value to the OTel attribute limit (4096 chars), appending a `[truncated]` marker. */
const truncateLogValue = (value: unknown): unknown => {
  let serialized: string | undefined;

  try {
    serialized = JSON.stringify(value);
  } catch {
    return '[unserializable]';
  }

  if (
    serialized === undefined ||
    serialized.length <= MAX_LOG_ATTRIBUTE_LENGTH
  ) {
    return value;
  }

  return `${serialized.slice(0, MAX_LOG_ATTRIBUTE_LENGTH)}… [truncated]`;
};

const buildRequestLogMessage = ({
  method,
  resolvedRequest,
  status,
}: {
  method: RequestMethod;
  resolvedRequest: ResolvedRequest;
  status: number;
}): string => {
  return `Request succeeded [${status} ${method} ${resolvedRequest.logicalPath}]`;
};

/**
 * Emits a structured success-path log record. Deferred via setTimeout so the
 * serialize+redact work happens after the response is handed back to the
 * caller — keeps the success path off the request latency budget while still
 * landing in Signoz logs for audit/observability.
 */
const logRequestSuccess = ({
  method,
  requestUrl,
  resolvedRequest,
  requestHeaders,
  body,
  responseData,
  status,
  durationMs,
}: {
  method: RequestMethod;
  requestUrl: URL;
  resolvedRequest: ResolvedRequest;
  requestHeaders: Headers;
  body?: RequestOptions['body'];
  responseData?: unknown;
  status: number;
  durationMs: number;
}): void => {
  setTimeout(() => {
    // Optional chaining keeps incomplete test mocks of @zoefin/logger
    // (e.g. {error, warn} only) from crashing this deferred callback.
    // Real pino logger always has .info.
    logger.info?.(
      {
        method,
        url: sanitizeUrlForLog(requestUrl),
        status,
        resolvedRequest: {
          ...resolvedRequest,
          url: sanitizeUrlForLog(resolvedRequest.url),
          headers: sanitizeHeadersForLog(resolvedRequest.headers),
        },
        headers: sanitizeHeadersForLog(requestHeaders),
        ...(body !== undefined && {body: redactSensitiveValue(body)}),
        ...(responseData !== undefined && {
          responseData: truncateLogValue(redactSensitiveValue(responseData)),
        }),
        durationMs: Math.round(durationMs),
      },
      buildRequestLogMessage({method, resolvedRequest, status}),
    );
  }, 0);
};

const logRequestFailure = ({
  error,
  method,
  requestUrl,
  resolvedRequest,
  requestHeaders,
  body,
  responseData,
  baseMessage,
  status,
  durationMs,
  level = 'error',
}: {
  error: RequestFailureLogContext;
  method: RequestMethod;
  requestUrl: URL;
  resolvedRequest: ResolvedRequest;
  requestHeaders: Headers;
  body?: RequestOptions['body'];
  responseData?: unknown;
  baseMessage: string;
  status?: number;
  durationMs?: number;
  level?: 'error' | 'warn';
}): void => {
  const message = buildRequestFailureLogMessage({
    baseMessage,
    method,
    requestUrl,
    resolvedRequest,
    status,
  });

  logger[level](
    {
      error,
      resolvedRequest: {
        ...resolvedRequest,
        url: sanitizeUrlForLog(resolvedRequest.url),
        headers: sanitizeHeadersForLog(resolvedRequest.headers),
      },
      headers: sanitizeHeadersForLog(requestHeaders),
      ...(body !== undefined && {body: redactSensitiveValue(body)}),
      ...(responseData !== undefined && {
        responseData: truncateLogValue(redactSensitiveValue(responseData)),
      }),
      ...(durationMs !== undefined && {durationMs: Math.round(durationMs)}),
    },
    message,
  );
  flushLogs();
};

/**
 * Core request pipeline: resolve route, inject auth, open OTel span, fetch, record response.
 *
 * Throws {@link RequestError} on HTTP errors or network failures.
 */
export const executeRequest = async ({
  url,
  options,
}: {
  url: string;
  options?: RequestOptions;
}): Promise<RequestRawResult> => {
  const method = options?.method ?? 'GET';
  const initialHeaders = withDefaultHeaders({
    headers: options?.headers,
    body: options?.body,
  });

  attachBrowserMetadata(initialHeaders);

  const resolvedRequest = await resolveRequest({
    url,
    headers: initialHeaders,
  });
  const requestUrl = generateValidUrl(resolvedRequest.url);
  const requestHeaders = new Headers(resolvedRequest.headers ?? initialHeaders);
  const span = startRequestSpan({
    method,
    requestUrl,
    resolvedRequest,
    headers: requestHeaders,
    body: options?.body,
    userContext: options?.userContext,
  });

  return withRequestSpanContext(span, async () => {
    // STREAM responses are long-lived (SSE / AI SDK Data Stream): a
    // single-shot `AbortSignal.timeout` would abort the connection mid-turn
    // (e.g. the matching-agent relay, whose turn can exceed the default 15s
    // when the Core matching algo endpoint is slow). Stream lifetime is
    // governed by the caller's upstream signal (client disconnect) + the
    // route's `maxDuration`, not a per-request timeout. Non-stream calls keep
    // the timeout guard.
    const {signal} =
      options?.responseType === RequestResponseType.STREAM
        ? {signal: options?.signal}
        : createRequestTimeoutSignal({
            upstreamSignal: options?.signal,
            timeoutMs: options?.timeoutMs,
          });

    injectTracingHeaders({
      headers: requestHeaders,
      resolvedRequest,
      requestUrl,
    });

    const missingClientCredentialsToken =
      resolvedRequest.authMode === RequestAuthMode.CLIENT_CREDENTIALS &&
      resolvedRequest.tokenSource === RequestAuthTokenSource.NONE &&
      !requestHeaders.get('authorization');

    if (missingClientCredentialsToken) {
      const tokenResolutionError = buildMissingClientCredentialsError({
        requestUrl,
        method,
      });

      setSpanError({
        span,
        error: tokenResolutionError,
        status: tokenResolutionError.status,
      });
      logRequestFailure({
        error: toFailureLogContext(tokenResolutionError),
        method,
        requestUrl,
        resolvedRequest,
        requestHeaders,
        body: options?.body,
        baseMessage: 'Request failed before fetch',
        status: tokenResolutionError.status,
      });

      throw tokenResolutionError;
    }

    // Tracks whether the request failed so the finally block can flush browser spans
    // immediately, preventing "missing span" gaps in the trace collector at Signoz
    let hasError = false;
    const fetchStartTime = performance.now();

    try {
      const response = await fetch(
        requestUrl,
        buildRequestInit({
          method,
          headers: requestHeaders,
          options: {
            ...options,
            signal,
          },
        }),
      );
      const parsedData = await parseResponseBody(
        response,
        // If the response is not ok, we don't want to parse the body
        response.ok ? options?.responseType : undefined,
      );

      setResponseAttributes({
        span,
        response,
        parsedData,
      });

      if (!response.ok) {
        hasError = true;

        const responseError = buildResponseError({
          response,
          requestUrl,
          method,
          parsedData,
        });

        setSpanError({
          span,
          error: responseError,
          status: response.status,
        });
        logRequestFailure({
          error: toFailureLogContext(responseError),
          method,
          requestUrl,
          resolvedRequest,
          requestHeaders,
          body: options?.body,
          responseData: parsedData,
          baseMessage: 'Request failed',
          status: responseError.status,
          durationMs: performance.now() - fetchStartTime,
          level: method === 'GET' ? 'warn' : 'error',
        });

        throw responseError;
      }

      logRequestSuccess({
        method,
        requestUrl,
        resolvedRequest,
        requestHeaders,
        body: options?.body,
        responseData: parsedData,
        status: response.status,
        durationMs: performance.now() - fetchStartTime,
      });

      return {
        response,
        resolvedRequest,
        parsedData,
      };
    } catch (error) {
      hasError = true;

      if (error instanceof RequestError) {
        throw error;
      }

      setSpanError({
        span,
        error,
      });

      const durationMs = performance.now() - fetchStartTime;

      const failureContext: RequestFailureLogContext = {
        message: error instanceof Error ? error.message : String(error),
        name: error instanceof Error ? error.name : undefined,
        stack: error instanceof Error ? error.stack : undefined,
        failureReason: classifyRequestFailure(error),
      };

      logRequestFailure({
        error: failureContext,
        method,
        requestUrl,
        resolvedRequest,
        requestHeaders,
        body: options?.body,
        baseMessage: 'Request execution failed',
        durationMs,
        level: method === 'GET' ? 'warn' : 'error',
      });

      throw new RequestError({
        message:
          error instanceof Error ? error.message : 'Unexpected request failure',
        url: requestUrl.toString(),
        method,
        cause: error,
      });
    } finally {
      span.end();

      if (hasError) {
        flushSpansOnError();
      }
    }
  });
};
